import {
    AccountStatementCoverage,
    HeldStatement,
    StatementPeriod,
} from 'services/bookkeeping/queries/useStatementCoverage';

/** Comparison key for a statement month. */
export function periodKey(period: StatementPeriod): string {
    return `${period.year}-${period.month}`;
}

/**
 * Identity of one tracker row: the document, plus the month it currently holds.
 *
 * A month correction moves a single row, so a document carrying several statements needs a key
 * per row rather than one per document — otherwise moving one month silently drops the others.
 *
 * `period` is absent for a document holding no row at all (one that failed to read, or lost its
 * month to another file), and that case deliberately keys on the bare uuid: it is the key such a
 * document has always had, so every path handling a document with no row — the couldn't-match
 * list, a staged correction on it, the saved-vs-changed diff — is untouched by row keying.
 */
export function rowKey(documentUuid: string, period?: StatementPeriod): string {
    return period ? `${documentUuid}:${periodKey(period)}` : documentUuid;
}

/** Chronological order, for any list of periods that reaches a member's eyes. */
export function sortPeriods(periods: StatementPeriod[]): StatementPeriod[] {
    return [...periods].sort((a, b) => a.year - b.year || a.month - b.month);
}

/**
 * Whether a list contains a month, by value. `StatementPeriod` is a plain object rebuilt on
 * every parse, so `includes` never matches.
 */
export function includesPeriod(periods: StatementPeriod[], period: StatementPeriod): boolean {
    const key = periodKey(period);
    return periods.some((candidate) => periodKey(candidate) === key);
}

/** One row a correction can address. `period` is absent for a document holding no row. */
export interface StatementRowRef {
    documentUuid: string;
    period?: StatementPeriod;
}

/**
 * Every row the modal can correct, in document order.
 *
 * One entry per tracker row, so a document carrying several statements contributes several — and
 * exactly one row-less entry for a document holding none, which is still correctable: the member
 * can give a month to a file we failed to read.
 *
 * Derived here rather than at each consumer so the rows the list draws, the rows the fold walks
 * and the rows the request is built from cannot disagree about what exists.
 */
export function statementRows(
    documentUuids: string[],
    heldPeriodsByUuid: Record<string, StatementPeriod[] | undefined>
): StatementRowRef[] {
    return documentUuids.flatMap((documentUuid) => {
        const held = heldPeriodsByUuid[documentUuid] ?? [];
        if (held.length === 0) {
            return [{ documentUuid }];
        }
        return sortPeriods(held).map((period) => ({ documentUuid, period }));
    });
}

/**
 * Stable identity for an account across renders and sessions. Not `bank_account_id`, which is
 * null for accounts Tight has not assigned an id to and so cannot tell two of them apart.
 * `plaid_institution_id` beats the display name so a rebrand doesn't orphan anything.
 */
export function accountKey(account: AccountStatementCoverage): string {
    return `${account.account_mask}-${account.plaid_institution_id ?? account.institution_name}`;
}

/** The subset of an account a name is resolved from, so a caller holding a narrower shape can use it. */
type NamedAccount = Pick<AccountStatementCoverage, 'institution_name' | 'account_name' | 'account_mask'>;

/**
 * What to call an account on screen, with no mask attached: "Chase".
 *
 * One definition because several surfaces name the same account, and a member who sees one of them
 * headless while another has a name reads it as two accounts, not one.
 *
 * `institution_name` leads because it is the name a member recognises, but it comes from the local
 * `PlaidItem` mirror and is blank for every non-Plaid account and any whose item we never mirrored
 * — the backend meters that share as `bk.statement_coverage.missing_institution_name`.
 * `account_name` is Tight's own label, populated independently of that mirror, so it covers exactly
 * that gap.
 *
 * The mask is not a guaranteed last resort — coverage keeps maskless accounts rather than hide a
 * real one from the ask, serializing the absent mask as `""` — so the chain ends on a generic
 * rather than returning the empty string that would collapse the heading this exists to fill.
 */
export function accountDisplayName(account: NamedAccount): string {
    return (
        account.institution_name?.trim() ||
        account.account_name?.trim() ||
        account.account_mask?.trim() ||
        'Bank account'
    );
}

/**
 * The same name with the mask alongside it: "Chase (3241)", or the mask alone.
 *
 * Composed from `accountDisplayName` rather than repeating its chain, so a surface rendering the
 * bare name and one rendering the label cannot disagree about which name won.
 *
 * The parenthetical is dropped where it would say nothing: when the name already is the mask
 * ("3241 (3241)"), and when there is no mask ("Chase ()" in prose the member reads).
 *
 * The mask is passed through as the API sends it — `AccountRow`, `AccountInfo` and the account
 * card all render it raw, and a member seeing it reformatted here and not there would read them
 * as two different accounts.
 */
export function statementAccountLabel(account: AccountStatementCoverage): string {
    const name = accountDisplayName(account);
    const mask = account.account_mask?.trim();
    return !mask || name === mask ? name : `${name} (${mask})`;
}

/**
 * The subset of `periods` that `held` does not cover.
 *
 * The same rule as `missingPeriods` over an arbitrary ask rather than the account's whole window,
 * for a caller scoped to fewer months than coverage requires — a back-books task asks only for the
 * months its own context names. Extracted rather than reimplemented so "still needed" keeps one
 * definition, which is what lets the card, the modal and the Continue gate agree.
 */
export function periodsNotHeld(periods: StatementPeriod[], held: StatementPeriod[]): StatementPeriod[] {
    const heldKeys = new Set(held.map(periodKey));
    return periods.filter((period) => !heldKeys.has(periodKey(period)));
}

/**
 * Months still outstanding: required minus held. The single definition of "still needed",
 * shared by the Continue gate, the card, the modal and the can't-supply confirmation, which
 * must all agree.
 */
export function missingPeriods(account: AccountStatementCoverage): StatementPeriod[] {
    return periodsNotHeld(account.required_periods, account.held_periods);
}

/**
 * The months credited to each document, grouped by document uuid.
 *
 * Grouped rather than one entry per document because a single PDF can carry several monthly
 * statements and holds a tracker row for each, so `held_periods` legitimately lists a document
 * more than once. Flattened to one entry, every month but the last silently disappeared.
 *
 * Entries with no `member_document_uuid` are dropped: they are months credited to a source that
 * carries no document (an aggregator feed), so there is no upload for a member to correct.
 */
export function groupHeldPeriods(held: HeldStatement[] | undefined): Record<string, StatementPeriod[]> {
    const grouped: Record<string, StatementPeriod[]> = {};
    (held ?? []).forEach(({ member_document_uuid: documentUuid, year, month }) => {
        if (!documentUuid) {
            return;
        }
        grouped[documentUuid] = [...(grouped[documentUuid] ?? []), { year, month }];
    });
    return grouped;
}

/**
 * Whether an account's ask is satisfied. An empty `required_periods` is not complete: the
 * books start date isn't resolved, so we don't know what to ask for yet. Callers that want to
 * let such an account through say so themselves.
 */
export function isAccountComplete(account: AccountStatementCoverage): boolean {
    return account.required_periods.length > 0 && missingPeriods(account).length === 0;
}
